/**
 * Экран «Код входа» / «Подтвердите телефон» (Figma 21:4837, 21:10034).
 * Состояние запроса кода хранится в sessionStorage вкладки, чтобы обновление
 * страницы вернуло тот же challenge и НЕ отправляло код повторно.
 * Сам код и пароль здесь не хранятся.
 */
export type OtpScreenFlow = 'login' | 'registration';

export type OtpScreenState = {
  flow: OtpScreenFlow;
  purpose: 'login' | 'register';
  phone: string;
  challengeId: string;
  channel: string;
  /** Момент (ms), когда истекает таймер смены способа. */
  cascadeUntil: number;
  error?: string;
  hasPassword?: boolean;
  savedAt: number;
};

const KEY = 'mt_otp_screen';
/** Старше этого - не восстанавливаем (код давно истек). */
const MAX_AGE_MS = 15 * 60 * 1000;

export function saveOtpScreen(
  state: Omit<OtpScreenState, 'savedAt' | 'cascadeUntil'> & { cascadeAfter?: number },
): void {
  const { cascadeAfter, ...rest } = state;
  const now = Date.now();
  const payload: OtpScreenState = {
    ...rest,
    cascadeUntil: now + Math.max(0, cascadeAfter ?? 0) * 1000,
    savedAt: now,
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // private mode / quota: экран просто не переживет обновление
  }
}

/** Обновить challenge/канал после повторной отправки, не трогая остальное. */
export function patchOtpScreen(
  flow: OtpScreenFlow,
  patch: { challengeId: string; channel: string; cascadeAfter?: number; error?: string },
): void {
  const prev = loadOtpScreen(flow);
  if (!prev) return;
  saveOtpScreen({
    ...prev,
    challengeId: patch.challengeId,
    channel: patch.channel,
    cascadeAfter: patch.cascadeAfter,
    error: patch.error ?? '',
  });
}

export function loadOtpScreen(flow: OtpScreenFlow): OtpScreenState | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as OtpScreenState;
    if (!data || data.flow !== flow || typeof data.phone !== 'string') return null;
    if (!data.savedAt || Date.now() - data.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/** Сколько секунд осталось до разблокировки смены способа (0 - уже можно). */
export function otpScreenSecondsLeft(state: OtpScreenState): number {
  return Math.max(0, Math.ceil((state.cascadeUntil - Date.now()) / 1000));
}

export function clearOtpScreen(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/** +7 (900) 000-00-00 для подписи экрана кода. */
export function formatOtpPhone(phone: string): string {
  const d = phone.replace(/\D/g, '');
  const n = d.length === 11 && (d[0] === '7' || d[0] === '8') ? d.slice(1) : d;
  if (n.length !== 10) return phone;
  return `+7 (${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6, 8)}-${n.slice(8, 10)}`;
}
